import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CardMembership as MembershipIcon,
  ConfirmationNumber as PackageIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useStore } from '../../context/StoreContext';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { ContactSupportFab } from '../common/ContactSupport';
import AddToHomeScreenPrompt from '../common/AddToHomeScreenPrompt';
import PushNotificationPrompt from '../notifications/PushNotificationPrompt';
import CustomerNotificationBell from './CustomerNotificationBell';
import { CustomerNotificationsProvider } from '../../context/CustomerNotificationsContext';
import CustomerOpenChatReadSync from './CustomerOpenChatReadSync';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

const focusRingSx = {
  outline: `2px solid ${BRAND.accent}`,
  outlineOffset: -2,
};

const desktopTabSx = (active) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  flexShrink: 0,
  px: 1.5,
  py: 1.75,
  border: 'none',
  borderBottom: `2px solid ${active ? BRAND.accent : 'transparent'}`,
  background: 'transparent',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '0.875rem',
  fontWeight: active ? 600 : 500,
  whiteSpace: 'nowrap',
  color: active ? BRAND.accentText : BRAND.textMuted,
  transition: 'color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    color: active ? BRAND.accentText : BRAND.text,
    borderBottomColor: active ? BRAND.accent : BRAND.line,
  },
  '&:focus-visible': focusRingSx,
});

const bottomNavItemSx = (active) => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.25,
  py: 0.75,
  color: active ? BRAND.accentText : BRAND.textMuted,
  '&:focus-visible': focusRingSx,
});

const CustomerLayout = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const { storeId, storeName, storeOverview } = useStore();
  const customerLogoUrl = storeOverview?.brandingImages?.customerLogoUrl;
  const [scrolled, setScrolled] = useState(false);

  const shellSx = {
    width: '100%',
    maxWidth: '1400px',
    mx: 'auto',
    px: { xs: 2, sm: 3, md: 4, lg: 4, xl: 4 },
    boxSizing: 'border-box',
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const hasActiveMembershipPlans = Boolean(storeOverview?.hasActiveMembershipPlans);
  const hasActiveServicePackages = Boolean(storeOverview?.hasActiveServicePackages);
  const homePath = buildCustomerRoute(storeId, '');
  const offerNavigationItems = [
    ...(hasActiveMembershipPlans
      ? [{ text: t('customer.navigation.memberships'), icon: MembershipIcon, path: buildCustomerRoute(storeId, 'memberships') }]
      : []),
    ...(hasActiveServicePackages
      ? [{ text: t('customer.navigation.packages'), icon: PackageIcon, path: buildCustomerRoute(storeId, 'packages') }]
      : []),
  ];
  const profileNavigationItem = { text: t('customer.navigation.profile'), icon: PersonIcon, path: buildCustomerRoute(storeId, 'customer-profile') };
  const appointmentsNavigationItem = { text: t('customer.navigation.appointments'), icon: CalendarIcon, path: buildCustomerRoute(storeId, 'my-appointments') };
  const desktopNavigationItems = [appointmentsNavigationItem, ...offerNavigationItems, profileNavigationItem];
  // The bottom bar always shows 3 items: Profile moves to the top bar only when both offers are active,
  // and Home fills the gap when there are no offers.
  const showProfileInTopBar = offerNavigationItems.length > 1;
  const bottomNavigationItems = showProfileInTopBar
    ? [appointmentsNavigationItem, ...offerNavigationItems]
    : [
      ...(offerNavigationItems.length === 0 ? [{ text: t('customer.navigation.home'), icon: HomeIcon, path: homePath }] : []),
      appointmentsNavigationItem,
      ...offerNavigationItems,
      profileNavigationItem,
    ];

  const isActive = (path) => location.pathname === path || location.pathname === `${path}/`;
  const isProfileActive = isActive(profileNavigationItem.path);

  return (
    <CustomerNotificationsProvider storeId={storeId}>
    <CustomerOpenChatReadSync />
    <Box
      sx={{
        minHeight: '100vh',
        background: BRAND.background,
        position: 'relative',
        pb: { xs: 'calc(88px + env(safe-area-inset-bottom))', md: 4 },
      }}
    >
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: scrolled ? `0 4px 16px ${brandAlpha('primaryDark', 0.18)}` : 'none',
          transition: 'box-shadow 0.3s ease',
        }}
      >
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <Box sx={{ bgcolor: BRAND.surface, color: BRAND.text, borderBottom: `1px solid ${BRAND.line}` }}>
            <Box
              sx={{
                ...shellSx,
                minHeight: { xs: 56, md: 64 },
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              {customerLogoUrl ? (
                <Box
                  component="img"
                  src={customerLogoUrl}
                  alt={storeName || undefined}
                  title={storeName || undefined}
                  onClick={() => navigate(homePath)}
                  sx={{
                    flex: '0 1 auto',
                    minWidth: 0,
                    width: 'auto',
                    maxWidth: { xs: 190, sm: 280 },
                    height: { xs: 38, md: 44 },
                    objectFit: 'contain',
                    objectPosition: 'left center',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              ) : (
                <Typography
                  variant="h6"
                  title={storeName || undefined}
                  onClick={() => navigate(homePath)}
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    m: 0,
                    fontWeight: 700,
                    fontSize: { xs: '1.05rem', sm: '1.25rem' },
                    lineHeight: 1.25,
                    letterSpacing: '0.01em',
                    color: BRAND.text,
                    cursor: 'pointer',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: { xs: 2, md: 1 },
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    '&:hover': { opacity: 0.85 },
                  }}
                >
                  {storeName || ' '}
                </Typography>
              )}

              <Box
                data-testid="customer-header-actions"
                style={{ marginLeft: 'auto' }}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
              >
                <CustomerNotificationBell storeId={storeId} />

                {isCompact && showProfileInTopBar ? (
                  <IconButton
                    onClick={() => navigate(profileNavigationItem.path)}
                    aria-label={profileNavigationItem.text}
                    aria-current={isProfileActive ? 'page' : undefined}
                    sx={{
                      flexShrink: 0,
                      color: isProfileActive ? BRAND.accentText : BRAND.textMuted,
                      bgcolor: isProfileActive ? brandAlpha('accent', 0.12) : 'transparent',
                      '&:hover': { bgcolor: brandAlpha('accent', 0.1), color: BRAND.accentText },
                    }}
                  >
                    <PersonIcon />
                  </IconButton>
                ) : null}
              </Box>
            </Box>
          </Box>

          {!isCompact ? (
            <Box sx={{ bgcolor: BRAND.surface, borderBottom: `1px solid ${BRAND.line}` }}>
              <Box
                component="nav"
                sx={{
                  ...shellSx,
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: 1,
                  overflowX: 'auto',
                }}
              >
                {desktopNavigationItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Box
                      key={item.path}
                      component="button"
                      type="button"
                      onClick={() => navigate(item.path)}
                      aria-current={active ? 'page' : undefined}
                      sx={desktopTabSx(active)}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      {item.text}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </motion.div>
      </Box>

      <Box sx={{ ...shellSx, pt: { xs: 2, md: 3 } }}>
        <AddToHomeScreenPrompt scopeId={storeId} />
        <PushNotificationPrompt audience="customer" />
        <Box
          sx={{
            background: BRAND.surface,
            borderRadius: BRAND_RADIUS.card,
            border: `1px solid ${BRAND.line}`,
            boxShadow: 'none',
            p: { xs: 2, sm: 3, md: 3, lg: 4 },
            minHeight: { xs: 'auto', lg: 'calc(100vh - 200px)' },
          }}
        >
          {children}
        </Box>
      </Box>

      <ContactSupportFab />

      {isCompact ? (
        <Box
          component="nav"
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            background: BRAND.surface,
            borderTop: `1px solid ${BRAND.line}`,
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          <Box
            sx={{
              maxWidth: '1400px',
              mx: 'auto',
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              px: 0.5,
              py: 0.5,
            }}
          >
            {bottomNavigationItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Box
                  key={item.path}
                  component="button"
                  type="button"
                  onClick={() => navigate(item.path)}
                  aria-label={item.text}
                  aria-current={active ? 'page' : undefined}
                  sx={bottomNavItemSx(active)}
                >
                  <Icon sx={{ fontSize: 22 }} />
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: active ? 700 : 500,
                      lineHeight: 1.2,
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : null}
    </Box>
    </CustomerNotificationsProvider>
  );
};

export default CustomerLayout;
